import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { mergePublicTheme, publicThemeVars } from '../publicTheme';
import PublicNavbar from './PublicNavbar';
import Carousel from './Carousel';
import { ReviewModal, CommentsModal, StarsDisplay, ratingSummary } from './Reviews';
import { usePublicSocket } from '../hooks/usePublicSocket';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-CO')}`;

// Detecta si una URL de la galería corresponde a un video (para reproducirlo
// en vez de mostrarlo como imagen).
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url || '');

// Devuelve los números de página a mostrar con elipsis: 1 … 4 5 [6] 7 8 … 20
function pageNumbers(current, totalPages) {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }
  return pages;
}

function Storefront() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Filtros del catálogo
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  // Filtros efectivamente aplicados: la petición al servidor solo usa estos, y
  // solo se actualizan al presionar "Buscar" (así no se consulta la BD en cada
  // tecla). Los estados de arriba son lo que el usuario está escribiendo/eligiendo.
  const [applied, setApplied] = useState({ search: '', category: '', brand: '', minPrice: '', maxPrice: '' });

  const [cart, setCart] = useState({});           // { [pk]: { qty, product } }
  const [cartOpen, setCartOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [view, setView] = useState('catalog');    // 'catalog' | 'checkout'
  const [form, setForm] = useState({ nombre: '', departamento: '', ciudad: '', direccion: '', celular: '', correo: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [otraCiudad, setOtraCiudad] = useState('');
  const [doneOrderId, setDoneOrderId] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);   // producto a calificar
  const [commentsTarget, setCommentsTarget] = useState(null); // producto cuyas opiniones se ven
  const [reviewRefresh, setReviewRefresh] = useState(0);
  // Se incrementa cuando el servidor avisa de un cambio (producto, carrusel,
  // config) para volver a cargar la tienda en tiempo real sin recargar la página.
  const [liveRefresh, setLiveRefresh] = useState(0);

  // Tiempo real: si el admin cambia un producto o la configuración/carrusel de la
  // empresa, refrescamos el catálogo sin que el cliente tenga que recargar.
  usePublicSocket(slug, ['product_changed', 'company_changed'], () => setLiveRefresh(n => n + 1));

  // Cargar tienda + productos (filtrado y paginado en el servidor). La petición
  // depende SOLO de los filtros aplicados (`applied`), que cambian al presionar
  // "Buscar" — no en cada tecla — para no saturar la base de datos.
  useEffect(() => {
    const params = new URLSearchParams();
    if (applied.search.trim()) params.set('q', applied.search.trim());
    if (applied.category) params.set('category', applied.category);
    if (applied.brand) params.set('brand', applied.brand);
    if (applied.minPrice) params.set('min', applied.minPrice);
    if (applied.maxPrice) params.set('max', applied.maxPrice);
    params.set('page', String(page));

    setLoading(true);
    fetch(`/api/tienda/${encodeURIComponent(slug)}?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        setStore(d);
        setProducts(d.products || []);
        setTotalProducts(d.total || 0);
        setTotalPages(d.totalPages || 1);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, applied, page, reviewRefresh, liveRefresh]);

  // Aplica los filtros actuales y vuelve a la página 1 (se dispara con "Buscar").
  const applyFilters = () => { setApplied({ search, category, brand, minPrice, maxPrice }); setPage(1); };
  const clearFilters = () => {
    setSearch(''); setCategory(''); setBrand(''); setMinPrice(''); setMaxPrice('');
    setApplied({ search: '', category: '', brand: '', minPrice: '', maxPrice: '' });
    setPage(1);
  };

  const addToCart = (product) => setCart(prev => {
    const current = prev[product.pk]?.qty || 0;
    // No permitir pedir más que el inventario disponible (cuando el producto lo controla)
    if (product.inventory != null && current >= product.inventory) return prev;
    return { ...prev, [product.pk]: { qty: current + 1, product } };
  });
  const removeFromCart = (pk) => setCart(prev => {
    const q = (prev[pk]?.qty || 0) - 1;
    const next = { ...prev };
    if (q <= 0) delete next[pk]; else next[pk] = { ...next[pk], qty: q };
    return next;
  });

  const cartLines = Object.values(cart).map(({ product, qty }) => ({ product, qty }));
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);
  const subtotal = cartLines.reduce((s, l) => s + l.product.price * l.qty, 0);

  // Domicilio por ciudad seleccionada
  const selectedCity = form.ciudad === '__otra__' ? otraCiudad : form.ciudad;
  const fee = useMemo(() => {
    if (!store) return 0;
    const per = store.domicile?.personalizado || {};
    if (selectedCity && per[selectedCity] !== undefined) return parseFloat(per[selectedCity]) || 0;
    return parseFloat(store.domicile?.general) || 0;
  }, [store, selectedCity]);

  const total = subtotal + fee;

  const openModal = (p) => { setModalProduct(p); setImgIndex(0); };
  const modalImages = modalProduct
    ? [modalProduct.image_link, ...(modalProduct.additional_image_links || [])].filter(Boolean)
    : [];

  // chosenMethod solo importa cuando la tienda acepta ambos ('both'); en los demás
  // casos el servidor ignora el valor y usa el método configurado por la empresa.
  const submitCheckout = async (chosenMethod) => {
    setErrorMsg('');
    const ciudadFinal = form.ciudad === '__otra__' ? otraCiudad.trim() : form.ciudad;
    if (!form.nombre || !form.departamento || !ciudadFinal || !form.direccion || !form.celular) {
      setErrorMsg('Completa todos los campos obligatorios.');
      return;
    }
    // Validar formato ANTES de enviar: si algo está mal, ni siquiera se crea el pedido
    // (y por tanto no se descuenta inventario). El servidor revalida por seguridad.
    const phoneDigits = form.celular.replace(/\D/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      setErrorMsg('El celular no es válido. Escribe un número de teléfono real.');
      return;
    }
    if (form.correo.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo.trim())) {
      setErrorMsg('El correo no es válido.');
      return;
    }
    setSubmitting(true);
    const res = await fetch(`/api/tienda/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: cartLines.map(l => ({ pk: l.product.pk, quantity: l.qty })),
        customer: { ...form, ciudad: ciudadFinal },
        payment_method: chosenMethod,
      }),
    });
    if (res.ok) {
      const d = await res.json();
      if (d.payment_method === 'cod') {
        // Contraentrega: el pedido ya quedó creado; mostrar confirmación con el número.
        setDoneOrderId(d.order_id || null);
        setView('done');
        setSubmitting(false);
      } else {
        window.location.href = d.checkout_url;
      }
    } else {
      const d = await res.json().catch(() => ({}));
      setErrorMsg(d.message || 'No se pudo procesar el pedido.');
      setSubmitting(false);
    }
  };

  const theme = mergePublicTheme(store?.theme);

  if (notFound) {
    return <main style={{ padding: '4rem 1.5rem', textAlign: 'center', color: theme.colors.textMuted }}>Tienda no encontrada.</main>;
  }
  if (!store) {
    return <main style={{ padding: '4rem 1.5rem', textAlign: 'center', color: theme.colors.textMuted }}>Cargando...</main>;
  }

  const cities = store.cities || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background, color: theme.colors.text, ...publicThemeVars(theme.colors) }}>
    <PublicNavbar slug={slug} store={store} current="tienda" maxWidth="1100px" />
    <main style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem 1.5rem' }}>
      {view === 'catalog' && (
        <>
          {/* Barra de filtros */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem', alignItems: 'center', marginBottom: '1.5rem', backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '0.9rem 1rem' }}>
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '200px' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.textMuted, fontSize: '0.95rem', pointerEvents: 'none' }}>🔍</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                placeholder="Buscar producto..."
                style={{ ...input, paddingLeft: '34px' }}
              />
            </div>
            {(store?.categories?.length > 0) && (
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...input, cursor: 'pointer', flex: '0 1 170px' }}>
                <option value="">Todas las categorías</option>
                {store.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {(store?.brands?.length > 0) && (
              <select value={brand} onChange={e => setBrand(e.target.value)} style={{ ...input, cursor: 'pointer', flex: '0 1 150px' }}>
                <option value="">Todas las marcas</option>
                {store.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            <input type="number" onWheel={e => e.currentTarget.blur()} min="0" value={minPrice} onChange={e => setMinPrice(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Precio mín." style={{ ...input, flex: '0 1 120px' }} />
            <input type="number" onWheel={e => e.currentTarget.blur()} min="0" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Precio máx." style={{ ...input, flex: '0 1 120px' }} />
            <button onClick={applyFilters} style={{ ...primaryBtn, flex: '0 0 auto', padding: '9px 20px' }}>🔍 Buscar</button>
            {(applied.search || applied.category || applied.brand || applied.minPrice || applied.maxPrice) && (
              <button onClick={clearFilters} style={{ ...secondaryBtn, flex: '0 0 auto' }}>Limpiar</button>
            )}
          </div>

          {/* Carrusel de imágenes */}
          {store.carousel?.images?.length > 0 && (
            <Carousel images={store.carousel.images} interval={store.carousel.interval} />
          )}

          {/* Rejilla de productos */}
          {loading && products.length === 0 ? (
            <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '3rem' }}>Cargando...</p>
          ) : products.length === 0 ? (
            <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '3rem' }}>No se encontraron productos con esos filtros.</p>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
            {products.map(p => (
              <div key={p.pk} style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div onClick={() => openModal(p)} style={{ cursor: 'pointer' }}>
                  {p.image_link
                    ? <img src={p.image_link} alt={p.title} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '160px', backgroundColor: theme.colors.background }} />}
                  <div style={{ padding: '0.7rem' }}>
                    <p style={{ color: theme.colors.text, fontSize: '0.9rem', fontWeight: 600, margin: '0 0 4px' }}>{p.title}</p>
                    <p style={{ color: theme.colors.primary, fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>{fmt(p.price)}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem', padding: '0.6rem' }}>
                  <button onClick={() => removeFromCart(p.pk)} style={qtyBtn}>−</button>
                  <span style={{ color: theme.colors.text, minWidth: '20px', textAlign: 'center' }}>{cart[p.pk]?.qty || 0}</span>
                  {(() => { const atMax = p.inventory != null && (cart[p.pk]?.qty || 0) >= p.inventory;
                    return <button onClick={() => addToCart(p)} disabled={atMax} style={{ ...qtyBtn, ...(atMax ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}>+</button>; })()}
                </div>
                {p.inventory != null && (cart[p.pk]?.qty || 0) >= p.inventory && (
                  <p style={{ color: theme.colors.textMuted, fontSize: '0.72rem', textAlign: 'center', margin: '0 0 0.5rem' }}>Máx. {p.inventory} disponible(s)</p>
                )}
                {(() => { const { avg, count } = ratingSummary(p.comments); return (
                  <div style={{ display: 'flex', gap: '0.4rem', padding: '0 0.6rem 0.6rem', borderTop: `1px solid ${theme.colors.border}22`, paddingTop: '0.6rem', flexWrap: 'wrap' }}>
                    <button onClick={() => setReviewTarget(p)} style={reviewBtn}>★ Calificar</button>
                    <button onClick={() => setCommentsTarget(p)} style={reviewBtnGhost}>
                      {count > 0 ? <><StarsDisplay value={avg} size={12} /> ({count})</> : 'Ver opiniones'}
                    </button>
                  </div>
                ); })()}
              </div>
            ))}
          </div>
          )}

          {/* Paginado */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1.8rem' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ ...pageBtn, opacity: page === 1 ? 0.4 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>← Anterior</button>
              {pageNumbers(page, totalPages).map((n, i) => (
                n === '…'
                  ? <span key={`e${i}`} style={{ color: theme.colors.textMuted, padding: '0 4px' }}>…</span>
                  : <button key={n} onClick={() => setPage(n)} style={{ ...pageBtn, ...(n === page ? { backgroundColor: theme.colors.primary, color: theme.colors.background, fontWeight: 700, borderColor: theme.colors.primary } : {}) }}>{n}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ ...pageBtn, opacity: page === totalPages ? 0.4 : 1, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Siguiente →</button>
            </div>
          )}
          {totalProducts > 0 && (
            <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: '0.82rem', marginTop: '0.8rem' }}>{totalProducts} producto(s) · página {page} de {totalPages}</p>
          )}

          {/* Botón flotante del carrito */}
          <button onClick={() => { setModalProduct(null); setCartOpen(true); }} style={{ position: 'fixed', bottom: '24px', right: '24px', width: '60px', height: '60px', borderRadius: '50%', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', cursor: 'pointer', fontSize: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 700 }}>
            🛒
            {cartCount > 0 && (
              <span style={{ position: 'absolute', top: '-4px', right: '-4px', backgroundColor: theme.colors.error, color: '#fff', borderRadius: '50%', width: '22px', height: '22px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cartCount}</span>
            )}
          </button>
        </>
      )}

      {/* Panel del carrito */}
      {cartOpen && view === 'catalog' && (
        <div style={overlay} onClick={() => setCartOpen(false)}>
          <div style={{ ...panel, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={panelHeader}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>Tu carrito</h3>
              <button onClick={() => setCartOpen(false)} style={closeBtn}>✕</button>
            </div>
            {cartLines.length === 0 ? (
              <p style={{ color: theme.colors.textMuted }}>Tu carrito está vacío.</p>
            ) : (
              <>
                {cartLines.map(l => (
                  <div key={l.product.pk} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: `1px solid ${theme.colors.border}22` }}>
                    <div>
                      <p style={{ color: theme.colors.text, margin: 0, fontSize: '0.88rem' }}>{l.product.title}</p>
                      <p style={{ color: theme.colors.textMuted, margin: 0, fontSize: '0.78rem' }}>{fmt(l.product.price)} c/u</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <button onClick={() => removeFromCart(l.product.pk)} style={qtyBtn}>−</button>
                      <span style={{ color: theme.colors.text }}>{l.qty}</span>
                      <button onClick={() => addToCart(l.product)} style={qtyBtn}>+</button>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', color: theme.colors.text, fontWeight: 700 }}>
                  <span>Subtotal</span><span>{fmt(subtotal)}</span>
                </div>
                <button onClick={() => { setCartOpen(false); setView('checkout'); }} style={{ ...primaryBtn, width: '100%', marginTop: '1rem' }}>
                  Finalizar compra
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de producto con carrusel */}
      {modalProduct && (
        <div style={overlay} onClick={() => setModalProduct(null)}>
          <div style={{ ...panel, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={panelHeader}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>{modalProduct.title}</h3>
              <button onClick={() => setModalProduct(null)} style={closeBtn}>✕</button>
            </div>
            {modalImages.length > 0 && (
              <div style={{ position: 'relative', textAlign: 'center', marginBottom: '1rem' }}>
                {isVideoUrl(modalImages[imgIndex]) ? (
                  <video
                    key={modalImages[imgIndex]}
                    src={modalImages[imgIndex]}
                    controls
                    autoPlay
                    playsInline
                    style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '8px' }}
                  />
                ) : (
                  <img src={modalImages[imgIndex]} alt="" style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '8px' }} />
                )}
                {modalImages.length > 1 && (
                  <>
                    <button onClick={() => setImgIndex(i => (i - 1 + modalImages.length) % modalImages.length)} style={{ ...arrowBtn, left: '8px' }}>◀</button>
                    <button onClick={() => setImgIndex(i => (i + 1) % modalImages.length)} style={{ ...arrowBtn, right: '8px' }}>▶</button>
                  </>
                )}
              </div>
            )}
            <p style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '1.1rem' }}>{fmt(modalProduct.price)}</p>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{modalProduct.description || 'Sin descripción.'}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem', marginTop: '1rem' }}>
              <button onClick={() => removeFromCart(modalProduct.pk)} style={qtyBtn}>−</button>
              <span style={{ color: theme.colors.text }}>{cart[modalProduct.pk]?.qty || 0}</span>
              {(() => { const atMax = modalProduct.inventory != null && (cart[modalProduct.pk]?.qty || 0) >= modalProduct.inventory;
                return <button onClick={() => addToCart(modalProduct)} disabled={atMax} style={{ ...qtyBtn, ...(atMax ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}>+</button>; })()}
            </div>
            {modalProduct.inventory != null && (
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', textAlign: 'center', marginTop: '0.5rem' }}>
                {modalProduct.inventory > 0 ? `${modalProduct.inventory} disponible(s)` : 'Agotado'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Checkout */}
      {view === 'checkout' && (
        <div style={{ maxWidth: '480px', margin: '0 auto' }}>
          <button onClick={() => setView('catalog')} style={{ ...secondaryBtn, marginBottom: '1rem' }}>← Volver</button>
          <h2 style={{ color: theme.colors.primary }}>Datos de envío</h2>
          {[
            { k: 'nombre', label: 'Nombre *' },
            { k: 'departamento', label: 'Departamento *' },
          ].map(f => (
            <div key={f.k} style={{ marginBottom: '0.8rem' }}>
              <label style={label}>{f.label}</label>
              <input value={form[f.k]} onChange={e => setForm({ ...form, [f.k]: e.target.value })} style={input} />
            </div>
          ))}
          <div style={{ marginBottom: '0.8rem' }}>
            <label style={label}>Ciudad *</label>
            <select value={form.ciudad} onChange={e => setForm({ ...form, ciudad: e.target.value })} style={{ ...input, cursor: 'pointer' }}>
              <option value="">— Selecciona tu ciudad —</option>
              {cities.map(ci => <option key={ci} value={ci}>{ci}</option>)}
              <option value="__otra__">Otra ciudad</option>
            </select>
          </div>
          {form.ciudad === '__otra__' && (
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={label}>Escribe tu ciudad *</label>
              <input value={otraCiudad} onChange={e => setOtraCiudad(e.target.value)} style={input} />
            </div>
          )}
          {[
            { k: 'direccion', label: 'Dirección *' },
            { k: 'celular', label: 'Celular *' },
            { k: 'correo', label: 'Correo (opcional)' },
          ].map(f => (
            <div key={f.k} style={{ marginBottom: '0.8rem' }}>
              <label style={label}>{f.label}</label>
              <input value={form[f.k]} onChange={e => setForm({ ...form, [f.k]: e.target.value })} style={input} />
            </div>
          ))}

          <div style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '10px', padding: '1rem', margin: '1rem 0' }}>
            <div style={rowLine}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div style={rowLine}><span>Domicilio</span><span>{fee > 0 ? fmt(fee) : '—'}</span></div>
            <div style={{ ...rowLine, fontWeight: 700, color: theme.colors.primary, fontSize: '1.05rem' }}><span>Total a pagar</span><span>{fmt(total)}</span></div>
          </div>

          {errorMsg && <p style={{ color: theme.colors.error, fontSize: '0.85rem' }}>{errorMsg}</p>}

          {(store.payment_method || 'online') === 'both' ? (
            <div>
              <p style={{ color: theme.colors.text, fontWeight: 600, marginBottom: '0.6rem' }}>¿Cómo deseas pagar?</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <button onClick={() => submitCheckout('online')} disabled={submitting || cartLines.length === 0} style={{ ...primaryBtn, width: '100%' }}>
                  {submitting ? 'Procesando...' : 'Pagar en línea'}
                </button>
                <button onClick={() => submitCheckout('cod')} disabled={submitting || cartLines.length === 0} style={{ ...secondaryBtn, width: '100%', padding: '11px 24px', fontWeight: 700, fontSize: '0.95rem' }}>
                  {submitting ? 'Procesando...' : 'Pagar contraentrega'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => submitCheckout(store.payment_method || 'online')} disabled={submitting || cartLines.length === 0} style={{ ...primaryBtn, width: '100%' }}>
              {submitting ? 'Procesando...' : ((store.payment_method || 'online') === 'cod' ? 'Confirmar pedido (contraentrega)' : 'Finalizar compra')}
            </button>
          )}
        </div>
      )}

      {/* Confirmación de pedido contraentrega */}
      {view === 'done' && (
        <div style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✅</div>
          <h2 style={{ color: theme.colors.primary, marginBottom: '0.8rem' }}>¡Pedido montado exitosamente!</h2>
          <p style={{ color: theme.colors.text, fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.2rem' }}>
            Cuando te llegue al domicilio puedes pagarlo contraentrega.
          </p>
          {doneOrderId != null && (
            <div style={{ backgroundColor: theme.colors.surface, border: `2px dashed ${theme.colors.primary}`, borderRadius: '12px', padding: '1.1rem', marginBottom: '1.5rem' }}>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', margin: '0 0 0.3rem' }}>📌 El número de tu pedido es</p>
              <div style={{ color: theme.colors.primary, fontSize: '2.2rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '1px' }}>#{doneOrderId}</div>
              <p style={{ color: theme.colors.text, fontSize: '0.88rem', lineHeight: 1.55, margin: '0.6rem 0 0' }}>
                ⭐ ¡Guarda este número! Con él podrás <strong>calificar y comentar</strong> el producto que compraste. No lo pierdas.
              </p>
            </div>
          )}
          <button
            onClick={() => { setCart({}); setForm({ nombre: '', departamento: '', ciudad: '', direccion: '', celular: '', correo: '' }); setOtraCiudad(''); setDoneOrderId(null); setView('catalog'); }}
            style={{ ...primaryBtn }}
          >
            Volver a la tienda
          </button>
        </div>
      )}

      {reviewTarget && (
        <ReviewModal
          mode="product" slug={slug} target={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => setReviewRefresh(n => n + 1)}
        />
      )}
      {commentsTarget && (
        <CommentsModal
          withRating heading={`Opiniones · ${commentsTarget.title}`}
          comments={commentsTarget.comments || []}
          onClose={() => setCommentsTarget(null)}
        />
      )}
    </main>
    </div>
  );
}

const qtyBtn = { width: '30px', height: '30px', borderRadius: '6px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', background: 'var(--pub-bg, #0f0f0f)', color: 'var(--pub-text, #ffffff)', cursor: 'pointer', fontSize: '1rem' };
const reviewBtn = { flex: '1 1 auto', padding: '6px 8px', borderRadius: '6px', border: 'none', background: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' };
const reviewBtnGhost = { flex: '1 1 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', background: 'none', color: 'var(--pub-text, #ffffff)', cursor: 'pointer', fontSize: '0.76rem', whiteSpace: 'nowrap' };
const primaryBtn = { padding: '11px 24px', backgroundColor: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' };
const secondaryBtn = { padding: '7px 16px', background: 'none', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', color: 'var(--pub-text, #ffffff)', borderRadius: '7px', cursor: 'pointer' };
const pageBtn = { minWidth: '36px', padding: '6px 12px', background: 'var(--pub-surface, #1a1a2e)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', color: 'var(--pub-text, #ffffff)', borderRadius: '7px', cursor: 'pointer', fontSize: '0.88rem' };
const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
const panel = { backgroundColor: 'var(--pub-surface, #1a1a2e)', borderRadius: '14px', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', padding: '1.5rem', width: '100%', maxHeight: '85vh', overflow: 'auto' };
const panelHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' };
const closeBtn = { background: 'none', border: 'none', color: 'var(--pub-muted, #94a3b8)', fontSize: '1.3rem', cursor: 'pointer' };
const arrowBtn = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer' };
const label = { display: 'block', color: 'var(--pub-muted, #94a3b8)', fontSize: '0.82rem', marginBottom: '0.3rem' };
const input = { width: '100%', padding: '9px 12px', backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56, 189, 248, 0.3))', borderRadius: '7px', color: 'var(--pub-text, #ffffff)', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' };
const rowLine = { display: 'flex', justifyContent: 'space-between', color: 'var(--pub-text, #ffffff)', fontSize: '0.9rem', padding: '3px 0' };

export default Storefront;
